import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';

export type RemoteSpeakerDayDto = {
  id: string;
  dayId: string;
  status: 'pending' | 'active';
  orderLineItemId?: string;
  requestedAt?: string;
  activatedAt?: string;
};

export type ConfigureRemoteSpeakerRequestDto = {
  scope: 'event' | 'flow' | 'session' | 'slot';
  enabled: boolean;
  flowId?: string;
  sessionId?: string;
  roleId?: string;
  slotId?: string;
  applyToExistingContributions?: boolean;
  defaultForNewRoles?: boolean;
  dayIds: string[];
  purchaseIfNeeded?: boolean;
};

export type ConfigureRemoteSpeakerResponseDto = {
  contributorId: string;
  remoteSpeakerEnabled: boolean;
  remoteSpeakerDefaultForNewRoles?: boolean;
  remoteSpeakerDays: RemoteSpeakerDayDto[];
  checkoutRequired?: boolean;
  neededQuota?: number;
  availableQuota?: number;
};

export type RemoteSpeakerScope = Extract<ConfigureRemoteSpeakerRequestDto['scope'], 'slot' | 'flow' | 'event'>;

/** Enable-modal UI scope; `manual` maps to event scope without applying to existing slots. */
export type RemoteSpeakerEnableUiScope = RemoteSpeakerScope | 'manual';

export function deriveRemoteSpeakerDayIdsForScope(
  contributor: ContributorDto,
  event: EventDto | undefined,
  scope: RemoteSpeakerScope | 'manual',
  params: { roleId?: string; slotId?: string },
): string[] {
  const resolvedScope = scope === 'manual' ? 'event' : scope;
  if (!event?.days?.length) return [];

  const dayIds = new Set<string>();

  const addDayFromEventSlotId = (eventSlotId: string | undefined) => {
    if (!eventSlotId) return;
    const eventSlot = event.slots?.find((entry) => entry.id === eventSlotId);
    if (!eventSlot?.day) return;
    const dayId = typeof eventSlot.day === 'string' ? eventSlot.day : eventSlot.day.id;
    if (dayId) dayIds.add(dayId);
  };

  const role = params.roleId ? contributor.roles?.find((entry) => entry.id === params.roleId) : undefined;

  if (scope === 'slot') {
    const slot = role?.slots?.find((entry) => entry.id === params.slotId && entry.deleted !== true);
    addDayFromEventSlotId(slot?.eventSlotId);
    return [...dayIds];
  }

  if (resolvedScope === 'flow') {
    for (const slot of role?.slots ?? []) {
      if (slot.deleted === true) continue;
      addDayFromEventSlotId(slot.eventSlotId);
    }
    return [...dayIds];
  }

  for (const contributorRole of contributor.roles ?? []) {
    for (const slot of contributorRole.slots ?? []) {
      if (slot.deleted === true) continue;
      addDayFromEventSlotId(slot.eventSlotId);
    }
  }

  return [...dayIds];
}

export function deriveSuggestedRemoteSpeakerDayIds(
  contributor: ContributorDto,
  event: EventDto | undefined,
  flowContext?: FlowDto,
): string[] {
  if (!event?.days?.length) return [];

  const dayIds = new Set<string>();
  for (const role of contributor.roles ?? []) {
    if (role.role === 'moderator' && role.entityType === 'Flow') {
      const flowId = typeof role.entity === 'string' ? role.entity : role.entity?.id;
      if (flowContext && flowId !== flowContext.id) continue;
      for (const slot of role.slots ?? []) {
        if (slot.eventSlotId) {
          const eventSlot = event.slots?.find((entry) => entry.id === slot.eventSlotId);
          const dayId = eventSlot?.day?.id ?? (typeof eventSlot?.day === 'string' ? eventSlot.day : undefined);
          if (dayId) dayIds.add(dayId);
        }
      }
    }
    if (role.role === 'speaker' && role.entityType === 'Session') {
      for (const slot of role.slots ?? []) {
        if (slot.eventSlotId) {
          const eventSlot = event.slots?.find((entry) => entry.id === slot.eventSlotId);
          const dayId = eventSlot?.day?.id ?? (typeof eventSlot?.day === 'string' ? eventSlot.day : undefined);
          if (dayId) dayIds.add(dayId);
        }
      }
    }
  }

  return [...dayIds];
}

export function contributorHasEffectiveRemoteSpeaker(contributor: ContributorDto): boolean {
  return (contributor.roles ?? []).some(
    (role) => role.effectiveRemoteSpeaker || (role.slots ?? []).some((slot) => slot.effectiveRemoteSpeaker),
  );
}

export function findRoleForScope(
  contributor: ContributorDto,
  scope: ConfigureRemoteSpeakerRequestDto['scope'],
  ids: { flowId?: string; sessionId?: string; roleId?: string },
): ContributorDto['roles'][number] | undefined {
  if (scope === 'slot' || scope === 'flow' || scope === 'session') {
    return contributor.roles.find((role) => role.id === ids.roleId);
  }
  return undefined;
}
